import { Money } from '../../components/ui/Money';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, Home, Car, Gift, DollarSign, Building2, CreditCard } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PortalPayroll = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/employee-portal/my-payroll`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('portal_token');
          navigate('/employee-portal/login');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
      </div>
    );
  }

  if (!data) return null;

  const salary = data.salary || {};
  const total = salary.total || 0;

  const breakdownItems = [
    { label: 'Basic Salary', value: salary.basic, icon: Wallet, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Housing Allowance', value: salary.housing, icon: Home, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Transportation', value: salary.transportation, icon: Car, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Other Allowances', value: salary.other, icon: Gift, color: 'text-purple-600', bg: 'bg-purple-50' },
  ];

  const getPercent = (val) => total > 0 ? ((val / total) * 100).toFixed(0) : 0;

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="portal-payroll">
      <h2 className="text-lg font-bold text-gray-900">Salary Structure</h2>

      {/* Total Salary Card */}
      <Card className="border-0 shadow-lg overflow-hidden" data-testid="total-salary-card">
        <div className="bg-gradient-to-br from-sky-500 via-blue-600 to-indigo-700 p-5 text-white">
          <p className="text-xs text-sky-200 uppercase tracking-wider">Total Monthly Salary</p>
          <p className="text-3xl font-bold mt-1"><Money value={total} digits={2} /></p>
          <div className="flex items-center gap-4 mt-3 text-xs text-sky-200">
            <span>{data.position}</span>
            <span>{data.department}</span>
          </div>
        </div>
        {/* Progress bar breakdown */}
        <div className="h-2 flex">
          {breakdownItems.map((item, i) => (
            <div
              key={i}
              className={`${['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-purple-500'][i]}`}
              style={{ width: `${getPercent(item.value)}%` }}
            />
          ))}
        </div>
      </Card>

      {/* Breakdown Cards */}
      {breakdownItems.map((item) => {
        const Icon = item.icon;
        return (
          <Card key={item.label} className="border-0 shadow-sm" data-testid={`salary-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center`}>
                    <Icon className={`h-5 w-5 ${item.color}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.label}</p>
                    <p className="text-[11px] text-gray-400">{getPercent(item.value)}% of total</p>
                  </div>
                </div>
                <p className="text-sm font-bold text-gray-900"><Money value={item.value} digits={2} /></p>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Payment Info */}
      {data.payment_method && (
        <Card className="border-0 shadow-sm" data-testid="payment-info-card">
          <CardContent className="p-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Payment Information</h3>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-gray-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Payment Method</p>
                <p className="text-xs text-gray-500">{data.payment_method}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PortalPayroll;
